/**
 * Helpers for "Academic & Ongoing Programmes" — itinerary items that recur over
 * multiple sessions (e.g. Mon–Fri language classes across several weeks).
 *
 * These items are pulled out of the day-by-day itinerary so a four-week course
 * is not presented as a single activity on its start day.
 */

export interface OngoingCandidate {
  session_count?: number | null;
  session_end_date?: string | null;
  session_weekdays?: number[] | null;
  date?: string | null;
  selected_time_slot?: any;
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];


export function isOngoingProgramme(item: OngoingCandidate): boolean {
  return !!item.session_count && item.session_count > 1;
}

/** Splits items into recurring programmes and the remaining day-linked items. */
export function splitOngoingProgrammes<T extends OngoingCandidate>(
  items: T[]
): { ongoing: T[]; dayItems: T[] } {
  const ongoing: T[] = [];
  const dayItems: T[] = [];
  for (const item of items) {
    if (isOngoingProgramme(item)) ongoing.push(item);
    else dayItems.push(item);
  }
  ongoing.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  return { ongoing, dayItems };
}

function parseAsUTC(dateStr: string): Date {
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}


const MON_FRI = [1, 2, 3, 4, 5];

function sessionDays(item: OngoingCandidate): number[] {
  return item.session_weekdays?.length ? [...item.session_weekdays].sort((a, b) => a - b) : MON_FRI;
}

/**
 * First and last session dates. Falls back to walking the chosen weekdays from
 * the start date for the number of sessions when no explicit end date is saved.
 */
export function sessionDateBounds(item: OngoingCandidate): { start: Date; end: Date | null } | null {
  if (!item.date) return null;
  const days = sessionDays(item);
  const cursor = parseAsUTC(item.date);
  // Move to the first day that actually runs.
  for (let i = 0; i < 7 && !days.includes(cursor.getUTCDay()); i++) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  const start = new Date(cursor.getTime());

  if (item.session_end_date) {
    const end = parseAsUTC(item.session_end_date);
    // Step back to the last day that actually runs.
    for (let i = 0; i < 7 && !days.includes(end.getUTCDay()); i++) {
      end.setUTCDate(end.getUTCDate() - 1);
    }
    return { start, end: end >= start ? end : start };
  }

  if (!item.session_count || item.session_count < 1) return { start, end: null };
  let remaining = item.session_count - 1;
  let guard = 0;
  while (remaining > 0 && guard < 5000) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    guard++;
    if (days.includes(cursor.getUTCDay())) remaining--;
  }
  return { start, end: new Date(cursor.getTime()) };
}

/** "Tuesday 5 to Thursday 28 January 2027" */
export function formatSessionDateSpan(item: OngoingCandidate): string | null {
  const bounds = sessionDateBounds(item);
  if (!bounds) return null;
  const { start, end } = bounds;
  const startWeekday = DAY_NAMES[start.getUTCDay()];
  if (!end || end.getTime() === start.getTime()) {
    return `${startWeekday} ${start.getUTCDate()} ${MONTH_NAMES[start.getUTCMonth()]} ${start.getUTCFullYear()}`;
  }
  const endWeekday = DAY_NAMES[end.getUTCDay()];
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();
  const startPart = sameMonth
    ? `${startWeekday} ${start.getUTCDate()}`
    : `${startWeekday} ${start.getUTCDate()} ${MONTH_NAMES[start.getUTCMonth()]}${sameYear ? "" : ` ${start.getUTCFullYear()}`}`;
  return `${startPart} to ${endWeekday} ${end.getUTCDate()} ${MONTH_NAMES[end.getUTCMonth()]} ${end.getUTCFullYear()}`;
}



/** "Monday to Friday" or "Mon, Wed, Fri" */
export function formatSessionWeekdays(weekdays?: number[] | null): string | null {
  if (!weekdays || weekdays.length === 0) return null;
  const sorted = [...weekdays].sort((a, b) => a - b);
  const consecutive = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  if (consecutive && sorted.length > 2) {
    return `${DAY_NAMES[sorted[0]]} to ${DAY_NAMES[sorted[sorted.length - 1]]}`;
  }
  return sorted.map((d) => DAY_SHORT[d]).join(", ");
}

const formatTime = (t: string) => t.slice(0, 5).replace(":", "h");

const toMin = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
};

/**
 * Billable hours per session: partial hours are rounded up to the full hour,
 * so a 09h00–12h50 class counts as 4 hours.
 */
export function sessionHours(item: OngoingCandidate): number | null {
  const slot = item.selected_time_slot;
  if (!slot?.start || !slot?.end) return null;
  const mins = toMin(slot.end) - toMin(slot.start);
  if (mins <= 0) return null;
  return Math.ceil(mins / 60);
}

/** "14h00 – 18h00 (4 hours per session)" */
export function formatSessionTiming(item: OngoingCandidate): string | null {
  const slot = item.selected_time_slot;
  if (!slot?.start || !slot?.end) return null;
  const hrs = sessionHours(item);
  const hours = hrs ? ` (${hrs} ${hrs === 1 ? "hour" : "hours"} per session)` : "";
  return `${formatTime(slot.start)} – ${formatTime(slot.end)}${hours}`;
}

/** "3 sessions per week, 12 sessions in total" */
export function formatSessionSummary(item: OngoingCandidate): string | null {
  if (!item.session_count) return null;
  const perWeek = item.session_weekdays?.length || 0;
  const total = `${item.session_count} ${item.session_count === 1 ? "session" : "sessions"} in total`;
  if (perWeek > 0 && item.session_count > perWeek) {
    return `${perWeek} ${perWeek === 1 ? "session" : "sessions"} per week, ${total}`;
  }
  return total;
}


/** Total instruction hours across all sessions, e.g. 80. */
export function totalSessionHours(item: OngoingCandidate): number | null {
  const hrs = sessionHours(item);
  if (!hrs || !item.session_count) return null;
  return hrs * item.session_count;
}

